import { v4 as uuidv4 } from 'uuid';
import type { Chord } from '@/types/chord';
import { getNaturalQuality } from '@/utils/chord-helpers';
import type { Move, MoveContext, MoveResult } from '../types';
import { rootPitchClass, nearestScaleDegreeForPitchClass, romanNumeral } from '../pitch-utils';

/**
 * take the long way home — secondary dominant. Inserts V/x before the
 * selected chord x.
 *
 * Beat rule (spec says "pick one rule, document it"): halves the TARGET
 * chord's own duration rather than stealing from the preceding gap — the
 * inserted dominant takes the first half, the target shrinks to the second
 * half and shifts its startBeat forward. This mirrors suspend-into-it and
 * avoids reaching into a third chord (the one before the target) that may
 * not have room to give up a beat.
 *
 * Root fidelity: V/x needs a root a perfect fifth above x. Scale degrees
 * are diatonic steps (see pitch-utils.ts), and going up a diatonic fifth
 * lands exactly on another diatonic step for every target EXCEPT the
 * diminished-position degree in each mode (vii° in major, ii° in minor) —
 * there the "fifth" is actually a tritone, so no diatonic degree can stand
 * in for the true applied-dominant root. applicable() checks for that exact
 * match and refuses those two degrees rather than silently mistuning them.
 */
export const takeTheLongWayHome: Move = {
  id: 'take-the-long-way-home',
  name: 'take the long way home',
  theory: 'secondary dominant',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    if (!chord || chord.isChromatic || chord.scaleDegree === 1) return false;
    if (chord.quality !== getNaturalQuality(chord.scaleDegree, ctx.mode)) return false;
    if (chord.duration <= 0) return false;

    const targetRootPc = rootPitchClass(ctx.key, chord.scaleDegree, ctx.mode);
    const dominantPc = (targetRootPc + 7) % 12;
    return nearestScaleDegreeForPitchClass(dominantPc, ctx.key, ctx.mode).exact;
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const targetRootPc = rootPitchClass(ctx.key, chord.scaleDegree, ctx.mode);
    const dominantPc = (targetRootPc + 7) % 12;
    const { degree: dominantDegree } = nearestScaleDegreeForPitchClass(dominantPc, ctx.key, ctx.mode);

    const firstDuration = chord.duration / 2;
    const secondDuration = chord.duration - firstDuration;

    const secondaryDominant: Chord = {
      ...chord,
      id: uuidv4(),
      scaleDegree: dominantDegree,
      quality: 'dom7',
      extensions: {},
      isChromatic: true,
      chromaticType: 'secondary',
      startBeat: chord.startBeat,
      duration: firstDuration,
      key: ctx.key,
      mode: ctx.mode,
    };
    const target: Chord = {
      ...chord,
      startBeat: chord.startBeat + firstDuration,
      duration: secondDuration,
      key: ctx.key,
      mode: ctx.mode,
    };

    const chords: Chord[] = [
      ...ctx.chords.slice(0, ctx.index),
      secondaryDominant,
      target,
      ...ctx.chords.slice(ctx.index + 1),
    ];

    const targetRoman = romanNumeral(chord.scaleDegree, ctx.mode);
    return {
      chords,
      changedIds: [secondaryDominant.id, target.id],
      explanation: `borrowed the key of ${targetRoman.toLowerCase()} for one beat to lean into it harder.`,
      theoryLabel: `secondary dominant · V/${targetRoman}`,
    };
  },
};
